import React, { useCallback, useMemo, useState } from 'react';
import FormControl from '@mui/material/FormControl';
import { CustomInputLabel } from '@zawarski/palmetto-ui-components';
import MSelect from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormHelperText from '@mui/material/FormHelperText';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import AddIcon from '@mui/icons-material/Add';
import PropTypes from 'prop-types';

const DropDownMenu = (props) => {
  const [searchText, setSearchText] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  const menuProps = {
    PaperProps: {
      style: {
        maxHeight: 400,
        paddingTop: 0,
      },
    },
    autoFocus: false,
  };

  // Filter values based on search text
  const filteredValues = useMemo(() => {
    if (!props.values || !props.values.length) return [];
    if (!searchText && !props.enableSearch) return props.values;

    const searchLower = searchText.toLowerCase();
    return props.values.filter((type) => {
      if (type && typeof type === 'object') {
        return type.value?.toLowerCase().includes(searchLower);
      } else {
        return type?.toLowerCase().includes(searchLower);
      }
    });
  }, [props.values, searchText, props.enableSearch]);

  const handleSearchChange = (e) => {
    const newSearchText = e.target.value;
    setSearchText(newSearchText);
  };

  const handleCreateNewDirectly = useCallback(() => {
    if (searchText.trim() && props.onCreateNew) {
      const newValue = searchText.trim();
      props.onCreateNew(newValue);

      // Auto-select the newly created option with a delay to ensure state updates
      setTimeout(() => {
        if (props.onChange && props.autoSelectNew !== false) {
          // Create a synthetic event that mimics MUI Select onChange
          const syntheticEvent = {
            target: { value: newValue, name: props.name },
          };
          props.onChange(syntheticEvent);
        }

        setSearchText('');
        setIsOpen(false); // Close the dropdown
      }, 100);
    }
  }, [searchText, props]);

  const showCreateOption = props.enableCreateNew && searchText && filteredValues.length === 0;

  return (
    <FormControl style={{ width: '100%', margin: 0 }}>
      <CustomInputLabel error={props.error} shrink htmlFor={props.id} style={{ margin: 0 }}>
        {props.label} <span className={props.required ? 'required' : ''}></span>{' '}
      </CustomInputLabel>
      <MSelect
        labelId='demo-multiple-checkbox-label'
        id='demo-multiple-checkbox'
        disabled={props.disabled}
        error={props.error}
        placeholder={props.placeholder}
        value={props.value}
        onChange={props.onChange}
        MenuProps={menuProps}
        variant='standard'
        open={isOpen}
        onOpen={() => setIsOpen(true)}
        onClose={() => {
          setSearchText('');
          setIsOpen(false);
        }}
        sx={{ marginTop: 0 }}>
        {props.enableSearch && (
          <Box
            onMouseDown={(e) => e.preventDefault()}
            sx={{
              px: 2,
              py: 1,
              position: 'sticky',
              top: 0,
              bgcolor: 'background.paper',
              zIndex: 1,
            }}>
            <TextField
              size='small'
              placeholder='Search...'
              fullWidth
              value={searchText}
              onChange={handleSearchChange}
              onKeyDown={(e) => {
                e.stopPropagation();
              }}
              onClick={(e) => {
                e.stopPropagation();
              }}
              autoFocus
            />
          </Box>
        )}

        {showCreateOption && (
          <Box
            component='li'
            role='option'
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleCreateNewDirectly();
            }}
            sx={{
              px: 2,
              py: 1.5,
              color: 'primary.main',
              fontWeight: 'bold',
              borderTop: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              userSelect: 'none',
              bgcolor: 'background.paper',
              '&:hover': {
                bgcolor: 'action.hover',
              },
              '&:active': {
                bgcolor: 'action.selected',
              },
            }}>
            <AddIcon sx={{ mr: 1 }} fontSize='small' />
            Create &quot;{searchText}&quot;
          </Box>
        )}

        {filteredValues && filteredValues.length
          ? filteredValues.map((type, index) => {
              if (type && typeof type === 'object') {
                return (
                  <MenuItem key={index} value={type.key}>
                    {type.value}
                  </MenuItem>
                );
              } else {
                return (
                  <MenuItem key={index} value={type}>
                    {type}
                  </MenuItem>
                );
              }
            })
          : !showCreateOption && (
              <MenuItem disabled>
                {searchText ? 'No results found' : 'No options available'}
              </MenuItem>
            )}
      </MSelect>
      {props.error ? (
        <FormHelperText error={props.error} style={{ margin: 0 }}>
          {props.helperTxt}
        </FormHelperText>
      ) : null}
    </FormControl>
  );
};

DropDownMenu.propTypes = {
  label: PropTypes.string,
  placeholder: PropTypes.string,
  value: PropTypes.any,
  onChange: PropTypes.func,
  values: PropTypes.array,
  disabled: PropTypes.bool,
  error: PropTypes.any,
  required: PropTypes.bool,
  id: PropTypes.string,
  name: PropTypes.string,
  helperTxt: PropTypes.string,
  enableSearch: PropTypes.bool,
  enableCreateNew: PropTypes.bool,
  onCreateNew: PropTypes.func,
  autoSelectNew: PropTypes.bool,
};

DropDownMenu.defaultProps = {
  enableSearch: false,
  enableCreateNew: false,
  autoSelectNew: true,
};

export default DropDownMenu;
